// Pipelines, stages and the board (ports of AppStore.pipeline(for:) / stage(for:) / stage(_:),
// GroundTeam/Store/AppStore.swift:181-186, and Records+Store.swift: pipelines(for:), defaultPipelineForBoard,
// pipelineRecords, pipelineColumns, openPipelineValue, weightedPipelineValue, commonPipeline).
//
// "First" means first in the array the caller passes (the replica's order); with the usual one default pipeline per
// record type the answer does not depend on it.

import type { RecordType } from '@/contracts/enums';
import { PRIORITY_SCORES } from '@/contracts/enums';
import { orderedStages, type PipelinePayload, type RecordPayload, type StagePayload } from '@/contracts/models';
import { compareLocalized, idKey, ms, sameId } from './util';

export { orderedStages };

/** `AppStore.pipeline(_:)`: the pipeline with this id. */
export function pipelineById(pipelines: readonly PipelinePayload[], id: string | null | undefined): PipelinePayload | undefined {
  return id ? pipelines.find((p) => sameId(p.id, id)) : undefined;
}

/**
 * `AppStore.pipeline(for:)`: the record's own `pipelineId`, else the default pipeline for its type, else the first
 * pipeline for its type.
 */
export function pipelineFor(
  record: Pick<RecordPayload, 'pipelineId' | 'type'>,
  pipelines: readonly PipelinePayload[],
): PipelinePayload | undefined {
  return (
    pipelineById(pipelines, record.pipelineId) ??
    pipelines.find((p) => p.recordType === record.type && p.isDefault) ??
    pipelines.find((p) => p.recordType === record.type)
  );
}

/** `Pipeline.stage(_:)`: the stage with this id inside one pipeline. */
export function stageIn(pipeline: Pick<PipelinePayload, 'stages'> | undefined, stageId: string | null | undefined): StagePayload | undefined {
  return pipeline && stageId ? pipeline.stages.find((s) => sameId(s.id, stageId)) : undefined;
}

/** `AppStore.stage(for:)`: the record's stage within `pipelineFor(record)` (undefined when it is not in it). */
export function stageFor(
  record: Pick<RecordPayload, 'pipelineId' | 'type' | 'stageId'>,
  pipelines: readonly PipelinePayload[],
): StagePayload | undefined {
  return stageIn(pipelineFor(record, pipelines), record.stageId);
}

/** `AppStore.stage(_:)`: a stage by id in any pipeline (stage-change titles, visit result links). */
export function findStage(pipelines: readonly PipelinePayload[], stageId: string | null | undefined): StagePayload | undefined {
  if (!stageId) return undefined;
  for (const p of pipelines) {
    const s = stageIn(p, stageId);
    if (s) return s;
  }
  return undefined;
}

/** `AppStore.pipelines(for:)`: a type's pipelines, the default first, then by name. */
export function pipelinesForType(pipelines: readonly PipelinePayload[], type: RecordType): PipelinePayload[] {
  return pipelines
    .filter((p) => p.recordType === type)
    .sort((a, b) => (a.isDefault !== b.isDefault ? (a.isDefault ? -1 : 1) : compareLocalized(a.name, b.name)));
}

/** `AppStore.defaultPipeline(for:)`. */
export function defaultPipelineForType(pipelines: readonly PipelinePayload[], type: RecordType): PipelinePayload | undefined {
  return pipelinesForType(pipelines, type)[0];
}

/** `defaultPipelineForBoard`: the default lead pipeline, else any default, else the first. */
export function defaultPipelineForBoard(pipelines: readonly PipelinePayload[]): PipelinePayload | undefined {
  return pipelines.find((p) => p.recordType === 'lead' && p.isDefault) ?? pipelines.find((p) => p.isDefault) ?? pipelines[0];
}

/** `pipelineRecords`: the stores (pass the visible ones) on this pipeline's board, optionally for one owner. */
export function pipelineRecords<R extends RecordPayload>(
  pipeline: Pick<PipelinePayload, 'id'>,
  records: readonly R[],
  pipelines: readonly PipelinePayload[],
  ownerId?: string | null,
): R[] {
  return records.filter((r) => {
    if (!sameId(pipelineFor(r, pipelines)?.id, pipeline.id)) return false;
    return ownerId ? sameId(r.ownerId, ownerId) : true;
  });
}

/** Row `modified_at` by record id: the board's "most recently updated" tie-break (iOS uses its local `updatedAt`). */
export type ModifiedAtLookup = (recordId: string) => string | undefined;

export interface PipelineColumn<R extends RecordPayload = RecordPayload> {
  stage: StagePayload;
  /** Hottest first: priority score, then most recently modified. */
  records: R[];
  /** Share of the board's stores in this column, 0–100. */
  percent: number;
  /** Sum of `value`. */
  value: number;
  /** `value × probability / 100`. */
  weightedValue: number;
}

export interface PipelineBoardOptions {
  ownerId?: string | null;
  modifiedAt?: ModifiedAtLookup;
}

/**
 * `pipelineColumns`: one column per stage by `order`, cards sorted by priority score (A first), then by row
 * `modified_at` descending. Stores whose stage is not in the pipeline are in no column (see `recordsWithoutStage`).
 */
export function pipelineColumns<R extends RecordPayload>(
  pipeline: PipelinePayload,
  records: readonly R[],
  pipelines: readonly PipelinePayload[],
  opts: PipelineBoardOptions = {},
): PipelineColumn<R>[] {
  const all = pipelineRecords(pipeline, records, pipelines, opts.ownerId);
  const total = Math.max(1, all.length);
  const modified = (r: R): number => ms(opts.modifiedAt?.(r.id)) ?? 0;
  return orderedStages(pipeline).map((stage) => {
    const rs = all
      .filter((r) => sameId(r.stageId, stage.id))
      .sort((a, b) => {
        const pa = PRIORITY_SCORES[a.priority] ?? 0;
        const pb = PRIORITY_SCORES[b.priority] ?? 0;
        if (pa !== pb) return pb - pa;
        return modified(b) - modified(a);
      });
    const value = rs.reduce((sum, r) => sum + r.value, 0);
    return {
      stage,
      records: rs,
      percent: (rs.length / total) * 100,
      value,
      weightedValue: (value * stage.probability) / 100,
    };
  });
}

/** Board stores whose `stageId` is not a stage of the pipeline (the "N stores without a stage" link). */
export function recordsWithoutStage<R extends RecordPayload>(
  pipeline: PipelinePayload,
  records: readonly R[],
  pipelines: readonly PipelinePayload[],
  ownerId?: string | null,
): R[] {
  return pipelineRecords(pipeline, records, pipelines, ownerId).filter((r) => !stageIn(pipeline, r.stageId));
}

/** `openPipelineValue`: value of board stores not in a won or lost stage (a store with no stage counts as open). */
export function openValue(
  pipeline: PipelinePayload,
  records: readonly RecordPayload[],
  pipelines: readonly PipelinePayload[],
  ownerId?: string | null,
): number {
  return pipelineRecords(pipeline, records, pipelines, ownerId).reduce((sum, r) => {
    const s = stageIn(pipeline, r.stageId);
    return !s || (!s.isWon && !s.isLost) ? sum + r.value : sum;
  }, 0);
}

/** `weightedPipelineValue`: Σ value × probability / 100 over open stores that have a stage. */
export function weightedValue(
  pipeline: PipelinePayload,
  records: readonly RecordPayload[],
  pipelines: readonly PipelinePayload[],
  ownerId?: string | null,
): number {
  return pipelineRecords(pipeline, records, pipelines, ownerId).reduce((sum, r) => {
    const s = stageIn(pipeline, r.stageId);
    return s && !s.isWon && !s.isLost ? sum + (r.value * s.probability) / 100 : sum;
  }, 0);
}

/**
 * Company-wide open and weighted pipeline (ReportEngine.summary): stores whose stage (via `stageFor`) is neither won
 * nor lost, a store without a stage included; weighted uses the stage probability (0 without a stage).
 */
export function pipelineTotals(
  records: readonly RecordPayload[],
  pipelines: readonly PipelinePayload[],
): { open: number; weighted: number; openCount: number } {
  let open = 0;
  let weighted = 0;
  let openCount = 0;
  for (const r of records) {
    const s = stageFor(r, pipelines);
    if (s?.isWon || s?.isLost) continue;
    open += r.value;
    weighted += (r.value * (s?.probability ?? 0)) / 100;
    openCount += 1;
  }
  return { open, weighted, openCount };
}

/** True when the record's stage is neither won nor lost (no stage counts as open). */
export function isOpenRecord(record: RecordPayload, pipelines: readonly PipelinePayload[]): boolean {
  const s = stageFor(record, pipelines);
  return s?.isWon !== true && s?.isLost !== true;
}

/** `commonPipeline(for:)`: the pipeline most of the given stores belong to (bulk stage changes). */
export function commonPipeline(records: readonly RecordPayload[], pipelines: readonly PipelinePayload[]): PipelinePayload | undefined {
  const counts = new Map<string, { p: PipelinePayload; n: number }>();
  for (const r of records) {
    const p = pipelineFor(r, pipelines);
    if (!p) continue;
    const k = idKey(p.id);
    const e = counts.get(k);
    if (e) e.n += 1;
    else counts.set(k, { p, n: 1 });
  }
  let best: { p: PipelinePayload; n: number } | undefined;
  for (const e of counts.values()) if (!best || e.n > best.n) best = e;
  return best?.p;
}
